import { Dot } from "@lcc/ui";
import { ApiProblem } from "../api/problem";
import { formatDateTime } from "../format/datetime";
import { Button } from "@lcc/ui";

/** Shows an API problem in plain language, with its stable `code` for support staff. */
export function ProblemAlert({ error }: { error: unknown }) {
  if (!error) return null;
  const problem = error instanceof ApiProblem ? error : null;
  const message = problem ? problem.userMessage : "Something went wrong. Please try again.";
  return (
    <div className="lcc-notice fer-notice" role="alert">
      <Dot tone="emergency" />
      <div className="lcc-notice__body">
        <p className="fer-notice__message">{message}</p>
        {problem && problem.fieldErrors.length > 0 && (
          <ul className="fer-notice__fields">
            {problem.fieldErrors.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        )}
        {problem && <p className="fer-notice__code">Reference code: {problem.code}</p>}
      </div>
    </div>
  );
}

/**
 * Old data shown after a failed refresh (review FW1): says plainly since when it has not been refreshed and why, so a
 * stale record is never taken for a live one.
 */
export function StaleNotice({ stale, refreshedAt, error, what }: { stale: boolean; refreshedAt: Date | null; error: unknown; what: string }) {
  if (!stale) return null;
  const problem = error instanceof ApiProblem ? error : null;
  return (
    <div className="lcc-notice fer-notice" role="alert" aria-label="Not refreshed">
      <Dot tone="warning" />
      <div className="lcc-notice__body">
        <p className="fer-notice__message">
          <strong>Not refreshed since {refreshedAt ? formatDateTime(refreshedAt.toISOString()) : "it was opened"}.</strong> This is the last {what} received; it may
          have changed since. Clocks are stopped until it refreshes.
        </p>
        <p>{problem ? problem.userMessage : "Something went wrong. Please try again."}</p>
        {problem && <p className="fer-notice__code">Reference code: {problem.code}</p>}
      </div>
    </div>
  );
}

export function Notice({ children, tone = "info" }: { children: React.ReactNode; tone?: "info" | "warning" }) {
  return (
    <div className="lcc-notice fer-notice" role="note">
      <Dot tone={tone === "warning" ? "warning" : "neutral"} />
      <div className="lcc-notice__body">{children}</div>
    </div>
  );
}

export function QueryState({
  isLoading,
  error,
  label,
}: {
  isLoading: boolean;
  error: unknown;
  label: string;
}) {
  if (isLoading) {
    return (
      <p className="fer-muted" role="status">
        Loading {label}…
      </p>
    );
  }
  return <ProblemAlert error={error} />;
}

/**
 * After an unknown outcome the request is locked (review R-W10): trying again sends exactly the same request (the
 * service applies it at most once); changing it needs an explicit discard, because the first may have been applied.
 */
export function UnknownOutcome({ write, retry }: { write: { locked: boolean; discard: () => void; isPending?: boolean }; retry?: () => void }) {
  if (!write.locked) return null;
  return (
    <div className="lcc-notice fer-notice" role="note">
      <Dot tone="warning" />
      <div className="lcc-notice__body">
        <p>
          <strong>The outcome is unknown</strong>: the request may already have been recorded. Sending again repeats exactly
          the same request (it is applied at most once), so the details are locked. To change them, check whether the first
          request went through, then discard it.
        </p>
        <div className="fer-actions fer-actions--start">
          {retry && (
            <Button size="sm" onClick={retry} disabled={write.isPending}>
              Try again (same request)
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={write.discard} disabled={write.isPending}>
            Discard and start again
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * After an unknown outcome of a write without an Idempotency-Key (the server guards it by the record's state), the
 * screen's data has been refreshed: the person checks it before sending again.
 */
export function UnknownUnkeyed({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="lcc-notice fer-notice" role="note">
      <Dot tone="warning" />
      <div className="lcc-notice__body">
        <p>
          <strong>The outcome is unknown</strong>: it may already have been recorded. The screen has been refreshed; check
          it before sending again.
        </p>
      </div>
    </div>
  );
}
